import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

interface CvThemeShadowRendererProps {
  html: string | null;
  isLoading: boolean;
  onSectionClick?: (section: string) => void;
  editable?: boolean;
  onLayoutReady?: () => void;
}

const A4_PAGE_HEIGHT_PX = 1123;
const PAGE_BREAK_PAD_PX = 40;
/** Reserved zone at the bottom of every page (logo lives there — never moved). */
const PAGE_BOTTOM_RESERVE_PX = 40;
/** Blocks up to this size near the boundary are moved entirely (page-bottom margin). */
const SMALL_BLOCK_PX = 64;
/** Usable content height per page (after top pad + bottom logo reserve). */
const USABLE_PAGE_PX = A4_PAGE_HEIGHT_PX - PAGE_BREAK_PAD_PX - PAGE_BOTTOM_RESERVE_PX;
const PAGINATION_MARKER = 'data-pg-pad';

/**
 * Deterministic A4 pagination for preview.
 *
 * Each page has a text area ending PAGE_BOTTOM_RESERVE_PX above its bottom
 * edge (logo zone). Content crossing that boundary is pushed to
 * `line + PAGE_BREAK_PAD_PX` (top of the next page + padding): page 1 ends
 * with a bottom margin, page 2 never starts glued, and the logo overlay stays
 * at the page bottom.
 *
 * Atomicity: `[data-splitable-item]` blocks that fit in one page are moved
 * whole (break-inside: avoid semantics), so items are never cut mid-content.
 * Multi-column themes are handled by recursing into every branch that crosses
 * the boundary, not just the first one.
 */
function applyDeterministicPagination(doc: Document): number {
  const content = doc.body;
  if (!content) return 1;

  // Reset margins injected by a previous run
  doc.querySelectorAll<HTMLElement>(`[${PAGINATION_MARKER}]`).forEach(el => {
    el.style.marginTop = '';
    el.removeAttribute(PAGINATION_MARKER);
  });

  const contentTop = content.getBoundingClientRect().top;
  const relTop = (el: HTMLElement): number => el.getBoundingClientRect().top - contentTop;

  const injectPad = (el: HTMLElement, targetTop: number): boolean => {
    const delta = targetTop - relTop(el);
    if (delta <= 0) return false;
    const current = Number.parseFloat(el.style.marginTop) || 0;
    el.style.marginTop = `${current + delta}px`;
    el.setAttribute(PAGINATION_MARKER, '');
    return true;
  };

  const padPageBreak = (line: number): boolean => {
    const boundary = line - PAGE_BOTTOM_RESERVE_PX;

    const fixCrossing = (el: HTMLElement): boolean => {
      const h = el.getBoundingClientRect().height;

      // Atomic item: if data-splitable-item crosses boundary and fits in one page, move whole item.
      if (el !== content && el.matches('[data-splitable-item]') && h <= USABLE_PAGE_PX) {
        return injectPad(el, line + PAGE_BREAK_PAD_PX);
      }

      const kids = (Array.from(el.children) as HTMLElement[]).filter(
        k => k.getBoundingClientRect().height > 0,
      );
      const crossing = kids.filter(k => {
        const t = relTop(k);
        const kh = k.getBoundingClientRect().height;
        return t <= boundary && t + kh > boundary;
      });

      // Recurse into ALL crossing children (handles multi-column themes).
      if (crossing.length > 0) {
        return crossing.map(fixCrossing).some(Boolean);
      }

      // `el` itself crosses the boundary (no child does).
      // Small leaf block near boundary: move it entirely.
      if (h <= SMALL_BLOCK_PX && boundary - relTop(el) <= SMALL_BLOCK_PX) {
        return injectPad(el, line + PAGE_BREAK_PAD_PX);
      }

      // Children cleanly split: push first child starting at/after the boundary.
      const target = kids.find(k => relTop(k) >= boundary);
      if (target) {
        if (relTop(target) >= line + PAGE_BREAK_PAD_PX) return false;
        return injectPad(target, line + PAGE_BREAK_PAD_PX);
      }

      // Tall leaf block: push next sibling (tail may remain).
      let node: HTMLElement | null = el;
      while (node && node !== content) {
        const sib = node.nextElementSibling as HTMLElement | null;
        if (sib && sib.getBoundingClientRect().height > 0) {
          if (relTop(sib) >= line + PAGE_BREAK_PAD_PX) return false;
          return injectPad(sib, line + PAGE_BREAK_PAD_PX);
        }
        node = node.parentElement;
      }
      return false;
    };

    return fixCrossing(content);
  };

  const measureMaxBottom = (): number => {
    let maxBottom = 0;
    const contentItems = doc.querySelectorAll<HTMLElement>(
      '[data-splitable-item], .cv-section-start, .cv-section-item, section, article, .item, .timeline-item, .card-nested, .cv-work-item, .cv-edu-item, .cv-skill-card, p, h1, h2, h3, h4, h5, ul, ol, dl, table, footer, header',
    );
    contentItems.forEach(el => {
      if (el.classList.contains('cv-edit-overlay') || el.classList.contains('cv-edit-btn')) return;
      const b = el.getBoundingClientRect();
      if (b.height === 0) return;
      const bottom = b.bottom - contentTop;
      if (bottom > maxBottom) maxBottom = bottom;
    });
    return maxBottom;
  };

  let pages = 1;
  for (let pass = 0; pass < 8; pass++) {
    const maxBottom = measureMaxBottom();
    pages = Math.max(1, Math.ceil(Math.max(0, maxBottom - 5) / A4_PAGE_HEIGHT_PX));
    let changed = false;
    for (let k = 1; k < pages; k++) {
      if (padPageBreak(k * A4_PAGE_HEIGHT_PX)) changed = true;
    }
    if (!changed) break;
  }
  return pages;
}

const EDIT_BUTTON_HTML = `<div class="cv-edit-overlay"><button class="cv-edit-btn" type="button"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg> Modifier</button></div>`;

const EDIT_STYLES = `
[data-cv-section] { position: relative; }
[data-cv-section]:hover { outline: 2px dashed #21c294; outline-offset: 4px; border-radius: 4px; }
.cv-edit-overlay {
  position: absolute; top: 8px; right: 8px;
  display: flex; gap: 4px; opacity: 1;
  pointer-events: auto; z-index: 10;
}
.cv-edit-btn {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 8px; border-radius: 6px;
  background: #21c294; color: white; border: none;
  font-size: 11px; font-weight: 600; cursor: pointer;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
  transition: transform 0.15s;
}
.cv-edit-btn:hover { background: #1a9e7a; transform: scale(1.05); }
.cv-edit-btn svg { width: 12px; height: 12px; }
`;

export function CvThemeShadowRenderer({
  html,
  isLoading,
  onSectionClick,
  editable = false,
  onLayoutReady,
}: CvThemeShadowRendererProps): JSX.Element {
  const { t } = useTranslation();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [pageCount, setPageCount] = useState<number>(1);
  const [iframeHeight, setIframeHeight] = useState<number>(A4_PAGE_HEIGHT_PX);

  useEffect((): (() => void) | undefined => {
    const iframe = iframeRef.current;
    if (!iframe || !html || isLoading) {
      return;
    }

    let cancelled = false;
    let clickCleanup: (() => void) | null = null;

    async function processIframeContent(): Promise<void> {
      if (!iframe) return;
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc || !doc.body) return;

      // Inject edit styles and edit overlays if in edit mode
      if (editable && onSectionClick) {
        if (!doc.getElementById('cv-edit-styles')) {
          const styleEl = doc.createElement('style');
          styleEl.id = 'cv-edit-styles';
          styleEl.textContent = EDIT_STYLES;
          doc.head?.appendChild(styleEl);
        }

        const sections = doc.querySelectorAll('[data-cv-section]');
        sections.forEach(sec => {
          if (!sec.querySelector('.cv-edit-overlay')) {
            sec.insertAdjacentHTML('beforeend', EDIT_BUTTON_HTML);
          }
        });

        const handleClick = (e: MouseEvent): void => {
          const target = e.target as HTMLElement | null;
          const section = target?.closest('[data-cv-section]');
          if (!section) return;
          const sectionId = section.getAttribute('data-cv-section');
          if (sectionId) {
            onSectionClick(sectionId);
          }
        };

        doc.addEventListener('click', handleClick);
        clickCleanup = (): void => {
          doc.removeEventListener('click', handleClick);
        };
      }

      // Prevent internal scrollbar in iframe and ensure 100% height fill
      if (!doc.getElementById('cv-no-overflow-styles')) {
        const noScrollStyle = doc.createElement('style');
        noScrollStyle.id = 'cv-no-overflow-styles';
        noScrollStyle.textContent = `
          html, body {
            min-height: 100% !important;
            height: 100% !important;
            background-color: #fff !important;
            overflow: hidden !important;
            scrollbar-width: none !important;
            -ms-overflow-style: none !important;
          }
          html::-webkit-scrollbar, body::-webkit-scrollbar {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
          }
        `;
        doc.head?.appendChild(noScrollStyle);
      }

      // Wait for fonts and images
      await doc.fonts.ready;
      await Promise.all(
        Array.from(doc.images).map((image: HTMLImageElement): Promise<void> => {
          if (image.complete) {
            return Promise.resolve();
          }
          return new Promise<void>(resolve => {
            image.addEventListener('load', () => resolve(), { once: true });
            image.addEventListener('error', () => resolve(), { once: true });
          });
        }),
      );

      // Settle layout
      await new Promise<void>(resolve => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => resolve());
        });
      });

      if (cancelled) return;

      const pages = applyDeterministicPagination(doc);
      setPageCount(pages);
      setIframeHeight(pages * A4_PAGE_HEIGHT_PX);

      onLayoutReady?.();
    }

    const handleLoad = (): void => {
      void processIframeContent();
    };

    iframe.addEventListener('load', handleLoad);

    // If iframe is already loaded
    if (iframe.contentDocument?.readyState === 'complete') {
      void processIframeContent();
    }

    return (): void => {
      cancelled = true;
      iframe.removeEventListener('load', handleLoad);
      if (clickCleanup) {
        clickCleanup();
      }
    };
  }, [html, isLoading, editable, onSectionClick, onLayoutReady]);

  return (
    <div className="w-full flex flex-col items-center">
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      )}
      <div className="relative w-[794px] min-h-[1123px] print:w-[210mm] print:min-h-[297mm]">
        <iframe
          ref={iframeRef}
          title={t('CV.preview')}
          srcDoc={html ?? undefined}
          scrolling="no"
          className="block w-[794px] border-0 bg-white rounded-lg shadow-sm print:w-[210mm] print:border-none print:shadow-none print:rounded-none print:m-0 print:p-0 overflow-hidden"
          sandbox="allow-same-origin allow-scripts"
          style={{
            width: '794px',
            height: `${iframeHeight}px`,
            minHeight: '1123px',
            overflow: 'hidden',
          }}
        />

        {/* Cibli logo at bottom right of each page */}
        {Array.from({ length: pageCount }, (_, i) => (
          <div
            key={`page-logo-${i + 1}`}
            className="absolute left-0 right-0 pointer-events-none z-20 print:hidden"
            style={{ top: i * A4_PAGE_HEIGHT_PX, height: A4_PAGE_HEIGHT_PX }}
          >
            <div className="absolute bottom-2 right-2">
              <img src="/logo.svg" alt="Cibli" className="w-15" />
            </div>
          </div>
        ))}

        {/* Cibli logo bottom right in print mode */}
        <div className="hidden print:fixed print:bottom-2 print:right-2 print:z-20 print:block print:pointer-events-none">
          <img src="/logo.svg" alt="Cibli" className="w-12" />
        </div>
      </div>
    </div>
  );
}
